import { useEffect, useState } from 'react';
import { BottomSheet } from '@/components/ui';
import { useAppUser } from '@/lib/useAppUser';
import { needsEmailVerification } from '@/lib/onboarding';
import { useDocumentsPending } from '@/lib/documents/useDocuments';
import {
  useAuthModal,
  useOpenLogin,
  useOpenSignup,
  useOpenVerifyEmail,
  useOpenForgotPassword,
  useCloseAuthModal,
} from '@/store/authModalStore';
import { useStartSignup, useFinishSignup } from '@/store/signupProgressStore';
import { useSignupTrack } from '@/analytics';
import { AuthLoginForm } from './AuthLoginForm';
import { AuthForgotPasswordForm } from './AuthForgotPasswordForm';
import { SignupSheet } from './SignupSheet';
import type { SignupStep } from './SignupStepper';

/**
 * Auth modal orchestrator.
 *
 * Reads the current mode from the auth modal store. Both login and signup
 * render inside a slide-up {@link BottomSheet} so the two flows share the
 * same chrome. Auto-closes when authentication succeeds.
 *
 * Mount once in the app shell (e.g. `App.tsx`).
 */
export function AuthModal() {
  const modal = useAuthModal();
  const openLogin = useOpenLogin();
  const openSignup = useOpenSignup();
  const openVerifyEmail = useOpenVerifyEmail();
  const openForgotPassword = useOpenForgotPassword();
  const close = useCloseAuthModal();
  const user = useAppUser();
  const startSignup = useStartSignup();
  const finishSignup = useFinishSignup();
  const track = useSignupTrack();
  const [signupStep, setSignupStep] = useState<SignupStep>('method');

  // A user dismiss of the signup sheet (backdrop / Escape / drag-down) is an
  // intentional exit — distinct from the programmatic close completion triggers,
  // which never invokes onClose. Split by phase: method = account creation,
  // terms = onboarding. The verification step lives in the separate
  // EmailVerificationPage, which reports its own dismiss.
  function handleSignupDismiss() {
    if (signupStep === 'method') track('sign_up_exited', { exited_at_step: 'method' });
    else if (signupStep === 'terms') track('onboarding_exited', { exited_at_step: 'terms' });
    close();
  }

  // Route a session that arrived through the *login* sheet. Where it goes is the
  // server's call, not the client's guess:
  //  - email still unverified → an old half-finished signup logged back in:
  //    hand off to the verification step so onboarding can complete.
  //  - pending consent documents exist → this is effectively a first signup (a
  //    social login auto-creates the Firebase account), so run the terms step.
  //  - nothing pending → a plain returning login: close, and clear any stale
  //    signup-in-progress flag so the app shell stops holding the lander.
  // Deciding off `/documents/pending` (instead of a client-side "is new?" guess)
  // is what stops the terms sheet from popping for existing accounts. If the
  // pending fetch errors we fail open and close — the server-side ConsentGuard +
  // TermsGate 403 backstop still enforce any consent we missed.
  const pendingQuery = useDocumentsPending(modal === 'login' && !!user && !needsEmailVerification(user), {
    refetchOnMount: 'always',
    staleTime: 0,
  });
  useEffect(() => {
    if (modal !== 'login' || !user) return;
    if (needsEmailVerification(user)) {
      startSignup();
      openVerifyEmail();
      return;
    }
    if (pendingQuery.isError) {
      finishSignup();
      close();
      return;
    }
    if (!pendingQuery.isSuccess) return;
    if (pendingQuery.data.length > 0) {
      startSignup();
      openSignup();
    } else {
      finishSignup();
      close();
    }
  }, [
    modal,
    user,
    pendingQuery.isSuccess,
    pendingQuery.isError,
    pendingQuery.data,
    startSignup,
    finishSignup,
    openVerifyEmail,
    openSignup,
    close,
  ]);

  return (
    <>
      <BottomSheet open={modal === 'login'} onClose={close}>
        <AuthLoginForm onSwitchMode={openSignup} onForgotPassword={openForgotPassword} onBack={close} />
      </BottomSheet>
      <BottomSheet open={modal === 'signup'} onClose={handleSignupDismiss}>
        <SignupSheet onStepChange={setSignupStep} onBack={handleSignupDismiss} />
      </BottomSheet>
      <BottomSheet open={modal === 'forgotPassword'} onClose={close}>
        <AuthForgotPasswordForm onBackToLogin={openLogin} onSignup={openSignup} />
      </BottomSheet>
    </>
  );
}
